import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Edit2, Search } from 'lucide-react';
import { universitiesAPI, countriesAPI, adminAPI } from '@/lib/api';
import UniversityComprehensiveEditForm from '@/components/admin/UniversityComprehensiveEditForm';
import { toast } from 'sonner';

/**
 * مدیریت جامع دانشگاه‌ها
 * امکان ویرایش کامل تمام فیلدها - مشابه مدیریت کشورها
 */
const UniversitiesComprehensiveManagement = () => {
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        await fetchData();
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [universitiesRes, countriesRes] = await Promise.all([
        universitiesAPI.getAll(),
        countriesAPI.getAll()
      ]);
      setUniversities(universitiesRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      toast.error('خطا در بارگذاری اطلاعات');
    }
  };

  const handleSave = async (universityId, data) => {
    try {
      // تبدیل آرایه‌های رشته‌ای به فرمت صحیح و پاک‌سازی داده‌ها
      const payload = {
        ...data,
        id: universityId,
        // تبدیل available_fields به array
        available_fields: Array.isArray(data.available_fields) 
          ? data.available_fields 
          : (data.available_fields || '').split(',').map(f => f.trim()).filter(Boolean),
        // تبدیل degrees به array
        degrees: Array.isArray(data.degrees)
          ? data.degrees
          : (data.degrees || '').split(',').map(d => d.trim()).filter(Boolean),
        // مطمئن شویم facilities آرایه است
        facilities: Array.isArray(data.facilities) ? data.facilities : [],
        // مطمئن شویم dormitories آرایه است
        dormitories: Array.isArray(data.dormitories) ? data.dormitories : [],
        // مطمئن شویم scholarships آرایه است
        scholarships: Array.isArray(data.scholarships) ? data.scholarships : []
      };
      
      await universitiesAPI.update(universityId, payload);
      await fetchData();
      setEditingUniversity(null);
      toast.success('دانشگاه با موفقیت به‌روز شد');
    } catch (error) {
      console.error('Save error:', error.response?.data || error);
      toast.error('خطا در به‌روزرسانی دانشگاه');
      throw error;
    }
  };

  const filteredUniversities = universities.filter(uni =>
    uni.name_fa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    uni.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    uni.city_fa?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#D4AF37] hover:text-[#F3E5AB]">
              <ArrowRight size={24} />
            </Link>
            <div>
              <h1 className="text-3xl font-light text-white">مدیریت جامع دانشگاه‌ها</h1>
              <p className="text-zinc-400 text-sm mt-1">ویرایش کامل تمام اطلاعات دانشگاه‌ها</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
            <input
              type="text"
              placeholder="جستجوی دانشگاه..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg pr-12 pl-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
        </div>

        {/* Universities Grid */}
        <div className="grid grid-cols-1 gap-4">
          {filteredUniversities.map(university => {
            const country = countries.find(c => c.id === university.country_id);
            return (
              <div
                key={university.id}
                className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
              >
                <div className="flex items-center justify-between">
                  {/* University Info */}
                  <div className="flex items-center gap-4 flex-1">
                    {university.logo_url && (
                      <img
                        src={university.logo_url}
                        alt={university.name_fa}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <h3 className="text-xl font-medium text-white">
                        {university.name_fa} ({university.name_en})
                      </h3>
                      <div className="flex items-center gap-6 mt-2 text-sm">
                        <span className="text-zinc-400">
                          📍 {university.city_fa}, {country?.name_fa}
                        </span>
                        {university.rank && (
                          <span className="text-zinc-400">
                            🏆 رنکینگ: {university.rank}
                          </span>
                        )}
                        {university.university_type && (
                          <span className="text-zinc-400">
                            🎓 {university.university_type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        {university.available_fields?.length > 0 && (
                          <span className="text-[#D4AF37]">
                            📚 {university.available_fields.length} رشته
                          </span>
                        )}
                        {university.scholarships?.length > 0 && (
                          <span className="text-[#D4AF37]">
                            🎖️ {university.scholarships.length} بورسیه
                          </span>
                        )}
                        {university.facilities?.length > 0 && (
                          <span className="text-[#D4AF37]">
                            🏢 {university.facilities.length} امکانات
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Edit Button */}
                  <button
                    onClick={() => setEditingUniversity(university)}
                    className="p-3 bg-white/5 hover:bg-[#D4AF37]/20 rounded-lg transition-colors"
                    title="ویرایش جامع"
                  >
                    <Edit2 size={20} className="text-[#D4AF37]" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredUniversities.length === 0 && (
          <div className="text-center text-zinc-400 py-12">
            هیچ دانشگاهی یافت نشد
          </div>
        )}

        {/* Edit Form Modal */}
        {editingUniversity && (
          <UniversityComprehensiveEditForm
            university={editingUniversity}
            countries={countries}
            onSave={handleSave}
            onCancel={() => setEditingUniversity(null)}
          />
        )}
      </div>
    </div>
  );
};

export default UniversitiesComprehensiveManagement;
